import * as React from "react";
import { ComposableMap, Geographies, Geography } from "react-simple-maps";
import { COUNTRY_NUMERIC_TO_ALPHA2 } from "@/lib/country-codes";

interface Props {
    /** ISO 3166-1 alpha-2 → session count */
    countries: { name: string; count: number }[];
}

const TOPOJSON_URL = "/world-atlas-110m.json";

/**
 * GA-style choropleth. Countries shaded by sessions on a 5-step scale; rest
 * stay neutral. Hover to see the count + flag.
 */
export function WorldMap({ countries }: Props): React.ReactNode {
    const byAlpha2 = React.useMemo(() => {
        const m = new Map<string, number>();
        for (const c of countries) m.set(c.name.toUpperCase(), c.count);
        return m;
    }, [countries]);

    const max = Math.max(0, ...countries.map((c) => c.count));
    const [hover, setHover] = React.useState<{ alpha2: string; count: number } | null>(null);

    return (
        <div className="relative">
            <ComposableMap
                projection="geoEqualEarth"
                projectionConfig={{ scale: 145 }}
                style={{ width: "100%", height: "auto", maxHeight: 360 }}
            >
                <Geographies geography={TOPOJSON_URL}>
                    {({ geographies }) =>
                        geographies.map((geo: any) => {
                            const numeric = String(geo.id).padStart(3, "0");
                            const alpha2 = COUNTRY_NUMERIC_TO_ALPHA2[numeric];
                            const count = alpha2 ? byAlpha2.get(alpha2) ?? 0 : 0;
                            const fill = colorForCount(count, max);
                            return (
                                <Geography
                                    key={geo.rsmKey}
                                    geography={geo}
                                    fill={fill}
                                    stroke="#ffffff"
                                    strokeWidth={0.4}
                                    style={{
                                        default: { outline: "none" },
                                        hover: {
                                            outline: "none",
                                            fill: count > 0 ? "#1d4ed8" : "#cbd5e1",
                                            cursor: count > 0 ? "pointer" : "default",
                                        },
                                        pressed: { outline: "none" },
                                    }}
                                    onMouseEnter={() => alpha2 && setHover({ alpha2, count })}
                                    onMouseLeave={() => setHover(null)}
                                />
                            );
                        })
                    }
                </Geographies>
            </ComposableMap>
            {hover && hover.count > 0 && (
                <div className="pointer-events-none absolute left-3 top-3 rounded-md border bg-background/95 px-3 py-1.5 text-xs shadow-md backdrop-blur">
                    <div className="font-medium">{flagOf(hover.alpha2)} {nameOf(hover.alpha2)}</div>
                    <div className="text-muted-foreground tabular-nums">{hover.count.toLocaleString()} sessions</div>
                </div>
            )}
            <Legend max={max} />
        </div>
    );
}

function Legend({ max }: { max: number }): React.ReactNode {
    if (max === 0) return null;
    const stops = [0, 0.2, 0.4, 0.6, 0.8, 1].map((p) => Math.round(max * p));
    return (
        <div className="mt-1 flex items-center justify-end gap-2 text-[10px] text-muted-foreground">
            <span>0</span>
            <div className="flex h-1.5 w-32 overflow-hidden rounded-full">
                {[1, 2, 3, 4, 5].map((step) => (
                    <div key={step} className="flex-1" style={{ background: SHADES[step] }} />
                ))}
            </div>
            <span className="tabular-nums">{stops[stops.length - 1].toLocaleString()}</span>
        </div>
    );
}

const SHADES: Record<number, string> = {
    0: "#f1f5f9", // slate-100
    1: "#dbeafe", // blue-100
    2: "#93c5fd", // blue-300
    3: "#3b82f6", // blue-500
    4: "#1d4ed8", // blue-700
    5: "#1e3a8a", // blue-900
};

function colorForCount(count: number, max: number): string {
    if (max === 0 || count === 0) return SHADES[0];
    const ratio = count / max;
    if (ratio < 0.05) return SHADES[1];
    if (ratio < 0.2) return SHADES[2];
    if (ratio < 0.5) return SHADES[3];
    if (ratio < 0.8) return SHADES[4];
    return SHADES[5];
}

function flagOf(alpha2: string): string {
    if (!alpha2 || alpha2.length !== 2) return "";
    return alpha2
        .toUpperCase()
        .split("")
        .map((c) => String.fromCodePoint(0x1f1a5 + c.charCodeAt(0)))
        .join("");
}

function nameOf(alpha2: string): string {
    try {
        return new Intl.DisplayNames(["en"], { type: "region" }).of(alpha2.toUpperCase()) ?? alpha2;
    } catch {
        return alpha2;
    }
}
